import React, { Fragment, useEffect } from 'react';
import { View } from 'react-native';
import { styled, SxProp, useSx } from 'dripsy';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import {
  EdgeInsets,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';

import {
  BaseText,
  Button,
  ButtonStyleEnum,
  Filters,
  ScreenLoader,
  Separator,
} from '@components';
import { useAsyncInit, useNavOptionsSet } from '@hooks';
import { useTutorialsModel } from '@models/Tutorials/TutorialsModel';
import { ToastService } from '@services';
import { parseApiError } from '@utils';
import {
  Category,
  SubCategory,
} from '@models/Tutorials/CategoryModel';
import { HubTagsContainer } from '@components/business/HubTagsContainer';
import type { Tag } from '@models/Tags/TagModel';
import { TabNavigatorNavigationPropType } from '@navigation';
import { useTagsModel } from '@models/Tags/TagsModel';

const Container = styled(View)({
  flex: 1,
  bg: '$background0',
});

function contentContainerStyle(insets: EdgeInsets): SxProp {
  return (theme) => ({
    flexGrow: 1,
    px: '$6',
    pt: '$6',
    pb: theme.space.$10 + insets.bottom,
  });
}

const listFooterComponentStyle: SxProp = {
  flex: 1,
  justifyContent: 'space-between',
};

const TextLabel = styled(BaseText)({
  color: '$heading800',
  variant: 'text.xlarge',
});

type NavigationPropType = TabNavigatorNavigationPropType<'Hub'>;

export const FiltersScreen = observer(() => {
  const { t } = useTranslation();
  const tutorial = useTutorialsModel();
  const tags = useTagsModel();
  const sx = useSx();
  const insets = useSafeAreaInsets();
  const nav = useNavigation<NavigationPropType>();

  useNavOptionsSet({ title: t('filter.title') });

  useAsyncInit(async () => {
    if (
      !tutorial.categories.fetch.isError &&
      tutorial.categories.fetch.hasEverBeenRun
    ) {
      return;
    }

    try {
      await tutorial.categories.fetch.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  });

  useEffect(() => {
    return () => {
      tutorial.categories.resetPreSelection();
    };
  }, []);

  async function apply() {
    try {
      nav.goBack();

      tutorial.categories.applyFilters();

      await tutorial.list.fetch.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  async function clear() {
    try {
      nav.goBack();

      tutorial.categories.clearFilters();
      tags.recommended.clearTagsSelection();

      await tutorial.list.fetch.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  function onChangeValue(
    item: Category | SubCategory,
    value: boolean,
  ) {
    item.setIsPreselected(value);
    // for now we should fetch videos by one category/tag parameter
    tags.recommended.clearTagsSelection();
  }

  if (tutorial.categories.fetch.inProgress) {
    return <ScreenLoader />;
  }

  function onChangeTagValue(item: Tag) {
    item.setIsSelected(!item.isSelected);
    // for now we should fetch videos by one category/tag parameter
    tutorial.categories.resetPreSelection();
  }

  return (
    <Container>
      <Filters
        withPreSelection
        onChangeValue={onChangeValue}
        contentContainerSx={sx(contentContainerStyle(insets))}
        data={tutorial.categories.filters}
        ListFooterComponentSx={listFooterComponentStyle}
        ListHeaderComponent={() => (
          <View>
            <TextLabel>{t('categories.title')}</TextLabel>
            <Separator size="$6" />
          </View>
        )}
        ListFooterComponent={() => (
          <Fragment>
            <HubTagsContainer onPress={onChangeTagValue} />
            <View>
              <Separator size="$8" />
              <Button
                onPress={apply}
                isLoading={tutorial.list.fetch.inProgress}
              >
                {t('filter.apply')}
              </Button>
              <Separator size="$5" />
              <Button
                onPress={clear}
                buttonStyle={ButtonStyleEnum.Ghost}
              >
                {t('filter.clear')}
              </Button>
            </View>
          </Fragment>
        )}
      />
    </Container>
  );
});
